import { queryOptions } from '@tanstack/react-query';
import { config } from '@shared/config';
import type {
  PublicCmsPageResponse,
  PublicCmsPagesResponse,
  CmsNavigationResponse,
  CmsPageType,
} from '@fanini/shared-types';

/**
 * CMS Query Factory — alle Query-Optionen für das CMS-System.
 * Nutzt das `queryOptions`-Pattern von TanStack Query v5.
 *
 * @example
 * ```tsx
 * const { data } = useQuery(cmsQueries.page('uber-uns'));
 * const { data } = useQuery(cmsQueries.navigation());
 * ```
 */
export const cmsQueries = {
  /**
   * Lädt eine einzelne CMS-Seite mit allen Sections und Blöcken.
   *
   * @param slug - URL-Slug der Seite (z.B. "uber-uns", "aktivitaeten/icon-league")
   */
  page: (slug: string) =>
    queryOptions<PublicCmsPageResponse>({
      queryKey: ['public', 'cms', 'pages', slug],
      queryFn: async () => {
        const res = await fetch(`${config.apiUrl}/public/cms/pages/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          throw new NotFoundError(`CMS-Seite "${slug}" nicht gefunden`);
        }
        if (!res.ok) {
          throw new Error(`CMS-Seite konnte nicht geladen werden: ${res.status}`);
        }
        return res.json() as Promise<PublicCmsPageResponse>;
      },
      staleTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof NotFoundError) return false;
        return failureCount < 2;
      },
    }),

  /**
   * Lädt alle publizierten CMS-Seiten, optional gefiltert nach Seiten-Typ.
   *
   * @param pageType - Optionaler Filter (z.B. "activity" für Aktivitäten-Seiten)
   */
  pages: (pageType?: CmsPageType) =>
    queryOptions<PublicCmsPagesResponse>({
      queryKey: ['public', 'cms', 'pages', { pageType }],
      queryFn: async () => {
        const url = new URL(`${config.apiUrl}/public/cms/pages`);
        if (pageType) url.searchParams.set('pageType', pageType);
        const res = await fetch(url.toString());
        if (!res.ok) {
          throw new Error(`CMS-Seitenliste konnte nicht geladen werden: ${res.status}`);
        }
        return res.json() as Promise<PublicCmsPagesResponse>;
      },
      staleTime: 5 * 60_000,
    }),

  /**
   * Lädt den Navigationsbaum für den Header.
   * 5-Minuten-Stale-Time — Navigation ändert sich selten.
   */
  navigation: () =>
    queryOptions<CmsNavigationResponse>({
      queryKey: ['public', 'cms', 'navigation'],
      queryFn: async () => {
        const res = await fetch(`${config.apiUrl}/public/cms/navigation`);
        if (!res.ok) {
          throw new Error(`Navigation konnte nicht geladen werden: ${res.status}`);
        }
        return res.json() as Promise<CmsNavigationResponse>;
      },
      staleTime: 5 * 60_000,
    }),
};

/**
 * Fehlerklasse für 404-Antworten der CMS-API.
 * Wird verwendet, um Retry-Verhalten in TanStack Query zu steuern.
 */
export class NotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}
